import Cookies from 'js-cookie';

const AUTH_COOKIE = 'authToken';
const USER_COOKIE = 'user';
const COOKIE_PATH = { path: '/' };

/**
 * Caminhos em que cookies da aplicação podem ter ficado "presos" em versões anteriores.
 * O navegador trata o mesmo nome em paths diferentes como cookies distintos, e
 * `Cookies.get(name)` resolve por uma ordem que pode devolver um valor antigo/inválido
 * enquanto qualquer um deles ainda existir — daí o sintoma "funciona em anônimo, falha no
 * navegador normal" após a migração para JWT.
 */
const LEGACY_COOKIE_NAMES = [AUTH_COOKIE, USER_COOKIE, 'userUUID'];
const LEGACY_COOKIE_PATHS = ['/', '/login', '/admin', '/app'];

function purgeAuthCookies() {
    const paths = new Set(LEGACY_COOKIE_PATHS);
    if (typeof window !== 'undefined' && window.location && window.location.pathname) {
        paths.add(window.location.pathname);
    }
    LEGACY_COOKIE_NAMES.forEach((name) => {
        Cookies.remove(name);
        paths.forEach((path) => {
            Cookies.remove(name, { path });
        });
    });
}

function decodePayload(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(padded)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(json);
    } catch (e) {
        return null;
    }
}

export function setAuthSession(token, user, expiresInMillis) {
    // Garante que não vai existir nenhuma cópia residual do cookie em outro path antes de gravar
    // o novo valor — senão `Cookies.get` pode devolver o cookie antigo (inválido) e o backend
    // responde 401 com a Bearer errada.
    purgeAuthCookies();
    const expires = expiresInMillis
        ? new Date(Date.now() + expiresInMillis)
        : new Date(Date.now() + 24 * 60 * 60 * 1000);
    Cookies.set(AUTH_COOKIE, token, { expires, ...COOKIE_PATH });
    if (user) {
        Cookies.set(USER_COOKIE, JSON.stringify(user), { expires, ...COOKIE_PATH });
    }
}

export function clearAuthSession() {
    purgeAuthCookies();
}

export function getAuthToken() {
    const token = Cookies.get(AUTH_COOKIE);
    if (!token) return null;
    // Qualquer coisa que não pareça um JWT (3 segmentos) é resíduo de versão anterior —
    // limpa a sessão para não enviar um Bearer inválido em cada request.
    if (token.split('.').length !== 3) {
        clearAuthSession();
        return null;
    }
    const payload = decodePayload(token);
    if (payload && typeof payload.exp === 'number' && payload.exp * 1000 < Date.now()) {
        clearAuthSession();
        return null;
    }
    return token;
}

export function isAuthenticated() {
    return !!getAuthToken();
}

export function getAuthHeader() {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}
